// src/components/FoodEntrySelection.tsx
import { useState } from 'react';
import { Camera, Edit3, Lock, AlertCircle, Sparkles, Zap } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';

interface FoodEntrySelectionProps {
  onSelectManual: () => void; 
  onSelectAI: () => void;
  onClose: () => void;
  onUpgrade?: () => void;
}

export default function FoodEntrySelection({ 
  onSelectManual, 
  onSelectAI, 
  onClose,
  onUpgrade 
}: FoodEntrySelectionProps) {
  const {
    canUseAIScanner,
    aiScansRemaining,
    subscriptionStatus,
    isExpired,
    isPro,
    checkAndIncrementAIScan,
  } = useSubscription();

  const [isCheckingUsage, setIsCheckingUsage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleAIScanSelect = async () => {
    // Check if user has expired subscription
    if (isExpired) {
      setErrorMessage('Your trial has expired. Please upgrade to continue using AI scans.');
      return;
    }

    // Check if user has remaining scans
    if (!canUseAIScanner) {
      setErrorMessage(`Daily AI scan limit reached (${subscriptionStatus?.daily_ai_limit || 4}). ${isPro ? 'Come back tomorrow!' : 'Upgrade to Pro for 8 scans/day!'}`);
      return;
    }

    setIsCheckingUsage(true);
    setErrorMessage('');

    try {
      const result = await checkAndIncrementAIScan();
      
      if (result.allowed) {
        onSelectAI();
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      console.error('Error checking usage:', error);
      setErrorMessage('Unable to verify usage. Please try again.');
    } finally {
      setIsCheckingUsage(false);
    }
  };

  const dailyLimit = subscriptionStatus?.daily_ai_limit || 4;
  const scansUsed = dailyLimit - aiScansRemaining;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div 
        className="bg-white w-full rounded-t-3xl p-6 max-w-2xl mx-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6"></div>
        
        <h2 className="text-xl font-bold text-gray-900 mb-2">Add Food</h2>
        <p className="text-sm text-gray-500 mb-4">Choose how you'd like to log your meal</p>

        {/* Usage indicator */}
        <div className="bg-gray-50 rounded-xl p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera size={16} className="text-cyan-600" />
            <span className="text-sm text-gray-600">AI Scans Today</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {[...Array(dailyLimit)].map((_, i) => (
                <div 
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i < scansUsed 
                      ? 'bg-cyan-500' 
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm font-semibold text-gray-900">
              {scansUsed}/{dailyLimit}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-700">{errorMessage}</p>
                {!isPro && onUpgrade && (
                  <button
                    onClick={onUpgrade}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-cyan-600 hover:text-cyan-700"
                  >
                    <Sparkles size={14} />
                    Upgrade to Pro
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {/* AI Scanner Option */}
          <button
            onClick={handleAIScanSelect}
            disabled={isCheckingUsage || isExpired}
            className={`w-full p-5 border-2 rounded-2xl transition-all group relative ${
              canUseAIScanner && !isExpired
                ? 'border-gray-200 hover:border-cyan-500 hover:bg-cyan-50'
                : 'border-gray-200 bg-gray-50 opacity-75'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 relative ${
                canUseAIScanner && !isExpired
                  ? 'bg-gradient-to-br from-cyan-500 to-blue-600'
                  : 'bg-gray-400'
              }`}>
                <Camera className="w-7 h-7 text-white" strokeWidth={2.5} />
                {(!canUseAIScanner || isExpired) && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-gray-600 rounded-full flex items-center justify-center">
                    <Lock size={10} className="text-white" />
                  </div>
                )}
              </div>
              <div className="text-left flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-gray-900 text-base">AI Photo Scanner</h3>
                  {!canUseAIScanner && !isExpired && (
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-[10px] font-bold rounded-full">
                      LIMIT REACHED
                    </span>
                  )}
                  {isExpired && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded-full">
                      TRIAL EXPIRED
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {canUseAIScanner && !isExpired
                    ? `Snap a photo • ${aiScansRemaining} scans left today`
                    : isExpired
                      ? 'Upgrade to continue scanning'
                      : `Daily limit reached (${dailyLimit})`
                  }
                </p>
              </div>
            </div>

            {/* Pro badge for non-pro users at limit */}
            {!isPro && !canUseAIScanner && !isExpired && (
              <div className="mt-3 flex items-center justify-center gap-2 pt-3 border-t border-gray-200">
                <Zap size={14} className="text-amber-500" />
                <span className="text-xs font-semibold text-gray-600">
                  Upgrade to Pro for 8 scans/day
                </span>
              </div>
            )}
          </button>

          {/* Manual Entry Option - Always available */}
          <button
            onClick={onSelectManual}
            className="w-full p-5 border-2 border-gray-200 rounded-2xl hover:border-gray-400 hover:bg-gray-50 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gray-900 flex items-center justify-center flex-shrink-0">
                <Edit3 className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
              <div className="text-left flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-gray-900 text-base">Manual Entry</h3>
                  <span className="px-2 py-0.5 bg-green-100 text-green-600 text-[10px] font-bold rounded-full">
                    UNLIMITED
                  </span>
                </div>
                <p className="text-sm text-gray-500">Enter nutrition information manually</p>
              </div>
            </div>
          </button>
        </div>

        {/* Upgrade CTA for non-pro users */}
        {!isPro && onUpgrade && (
          <button
            onClick={onUpgrade}
            className="w-full mt-4 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-bold text-sm hover:from-amber-500 hover:to-orange-600 transition-all flex items-center justify-center gap-2"
          >
            <Sparkles size={16} />
            Upgrade to Pro for More Scans
          </button>
        )}

        <button
          onClick={onClose}
          className="w-full mt-3 px-6 py-3 text-gray-600 hover:text-gray-900 font-semibold transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}